// Animated status for a ticket's coding job: what stage it's at, and whether
// an agent is working, people are still discussing, or a super admin is needed.
import { Bot, Check, Clock, MessageSquare, Square } from 'lucide-react';
import { CODING_STAGES, codingStatus, waitingLabel } from '../lib/codingStatus';
import type { CodingJobState } from '../types';

function Dots({ className }: { className: string }) {
  return (
    <span className="inline-flex items-center gap-1" aria-hidden>
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className={`w-1.5 h-1.5 rounded-full animate-bounce ${className}`}
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </span>
  );
}

export function CodingAgentStatus({
  state, canDecide, model, agentAvailable = true,
}: { state: CodingJobState; canDecide: boolean; model?: string; agentAvailable?: boolean }) {
  const status = codingStatus(state, agentAvailable);
  const headline = waitingLabel(state, canDecide, agentAvailable);

  return (
    <div className="space-y-3" role="status" aria-live="polite">
      {status.mood === 'working' && (
        <div className="rounded-xl bg-navy/5 border border-navy/10 p-3">
          <div className="flex items-center gap-3">
            <span className="relative flex w-9 h-9 items-center justify-center rounded-full bg-navy text-white shrink-0">
              <span className="absolute inset-0 rounded-full bg-navy/40 animate-ping" />
              <Bot size={18} className="relative" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-navy flex items-center gap-2">
                {headline}
                <Dots className="bg-navy" />
              </p>
              {model && <p className="text-xs text-gray-500">Running on {model}</p>}
            </div>
          </div>
          <div className="agent-working-bar relative mt-3 h-1.5 rounded-full bg-navy/10 overflow-hidden" />
        </div>
      )}

      {status.mood === 'waiting' && (
        <div className="rounded-xl bg-gold/10 border border-gold/40 p-3 flex items-center gap-3">
          <span className="agent-waiting-glow flex w-9 h-9 items-center justify-center rounded-full bg-gold text-navy shrink-0">
            <Clock size={18} />
          </span>
          <p className="text-sm font-semibold text-navy">{headline}</p>
        </div>
      )}

      {status.mood === 'discussing' && (
        <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 flex items-center gap-3">
          <span className="flex w-9 h-9 items-center justify-center rounded-full bg-gray-200 text-gray-600 shrink-0">
            <MessageSquare size={17} />
          </span>
          <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
            {headline}
            <Dots className="bg-gray-400" />
          </p>
        </div>
      )}

      {status.mood === 'done' && (
        <div className="rounded-xl bg-green-50 border border-green-200 p-3 flex items-center gap-3">
          <span className="flex w-9 h-9 items-center justify-center rounded-full bg-green-600 text-white shrink-0">
            <Check size={18} />
          </span>
          <p className="text-sm font-semibold text-green-800">{headline}</p>
        </div>
      )}

      {status.mood === 'stopped' && (
        <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 flex items-center gap-3">
          <span className="flex w-9 h-9 items-center justify-center rounded-full bg-gray-300 text-gray-600 shrink-0">
            <Square size={14} />
          </span>
          <p className="text-sm font-medium text-gray-600">{headline}</p>
        </div>
      )}

      <ol className="flex flex-wrap items-center gap-1" aria-label="Coding job stages">
        {CODING_STAGES.map((label, index) => {
          const past = index < status.stage || (index === status.stage && status.mood === 'done');
          const current = index === status.stage && status.mood !== 'done';
          const dot = past
            ? 'bg-navy text-white'
            : current
              ? status.mood === 'waiting'
                ? 'bg-gold text-navy agent-waiting-glow'
                : status.mood === 'working'
                  ? 'bg-navy text-white animate-pulse'
                  : status.mood === 'stopped'
                    ? 'bg-gray-300 text-gray-600'
                    : 'bg-gray-200 text-gray-600'
              : 'bg-gray-100 text-gray-400';
          return (
            <li key={label} className="flex items-center gap-1 shrink-0">
              <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold ${dot}`}>
                {past && <Check size={11} />}
                {label}
              </span>
              {index < CODING_STAGES.length - 1 && (
                <span className={`w-3 h-px ${index < status.stage ? 'bg-navy' : 'bg-gray-200'}`} />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Compact live indicator for the ticket list. */
export function CodingAgentDot({ state }: { state: CodingJobState }) {
  const status = codingStatus(state);
  const color = status.mood === 'working'
    ? 'bg-navy'
    : status.mood === 'waiting'
      ? 'bg-gold'
      : status.mood === 'done'
        ? 'bg-green-600'
        : 'bg-gray-400';
  const animated = status.mood === 'working' || status.mood === 'waiting';
  return (
    <span className="relative inline-flex w-2.5 h-2.5 shrink-0" aria-hidden>
      {animated && <span className={`absolute inset-0 rounded-full ${color} opacity-60 animate-ping`} />}
      <span className={`relative inline-flex w-2.5 h-2.5 rounded-full ${color}`} />
    </span>
  );
}
